import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AcceptResourceInviteAction,
  ValidateResourceInviteAction,
} from "../../store/action";
import AuthShell from "../ui/AuthShell";
import {
  fieldClass,
  fieldErrorClass,
  labelClass,
  primaryButtonClass,
  textLinkClass,
} from "../ui/formStyles";

/** Mirrors the API's minimum (shared with signup/reset) so the form rejects
 * what the API would reject. */
const MIN_PASSWORD_LENGTH = 8;

/**
 * Landing page for the emailed resource-invite link (/accept-invite?token=...).
 *
 * Checks the link first and only then offers a way to act on it — either a
 * password form (brand-new account) or a one-click confirmation (an existing
 * account in the same organization gets auto-linked). Mirrors the
 * checking → act → done/invalid phase shape of ResetPassword.jsx.
 */
const AcceptInvite = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [phase, setPhase] = useState("checking");
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setPhase("invalid");
      return;
    }

    let cancelled = false;

    const check = async () => {
      try {
        const data = await ValidateResourceInviteAction(token);
        if (cancelled) return;

        if (!data?.valid) {
          setPhase("invalid");
          return;
        }

        setInvite(data);

        // The link itself is genuinely valid here — it's the email behind it
        // that's already tied to a different organization, so accepting can
        // only ever fail. Say that up front instead of showing a form (or an
        // auto-link confirmation) that is guaranteed to be rejected on submit.
        if (data.emailBelongsToAnotherOrganization) {
          setPhase("blocked");
          return;
        }

        setPhase(data.requiresPassword ? "form" : "confirm");
      } catch (err) {
        if (cancelled) return;
        setPhase("invalid");
      }
    };

    check();

    // Validation is read-only, so this is safe to run twice — the flag only
    // stops a late response from overwriting newer state.
    return () => {
      cancelled = true;
    };
  }, [token]);

  const isActing = phase === "form" || phase === "confirm";

  return (
    <AuthShell className="accept-invite-content w-full max-w-md">
      <div className="text-center">
        <span
          className={`inline-flex size-12 items-center justify-center rounded-xl ${
            phase === "invalid" || phase === "blocked"
              ? "bg-red-50 text-red-600"
              : "bg-brand-50 text-brand-600"
          }`}
        >
          <i
            className={`bx ${phase === "done" ? "bx-check" : "bx-user-plus"} text-2xl`}
            aria-hidden="true"
          />
        </span>

        <h1 className="accept-invite-title mt-4 text-fluid-xl font-bold tracking-tight text-ink-900">
          {phase === "checking" && t("AcceptInvite.checking")}
          {phase === "form" && t("AcceptInvite.formTitle")}
          {phase === "confirm" && t("AcceptInvite.confirmTitle")}
          {phase === "done" && t("AcceptInvite.doneTitle")}
          {phase === "invalid" && t("AcceptInvite.invalidTitle")}
          {phase === "blocked" && t("AcceptInvite.blockedTitle")}
        </h1>

        {isActing && invite && (
          <p className="mt-2 text-fluid-sm text-ink-500">
            {t("AcceptInvite.subtitle", {
              resourceName: invite.resourceName,
              organizationName: invite.organizationName,
            })}
          </p>
        )}

        {phase === "invalid" && (
          <p className="mt-2 text-fluid-sm text-ink-500" role="alert">
            {error || t("AcceptInvite.invalidBody")}
          </p>
        )}

        {phase === "blocked" && (
          <p className="mt-2 text-fluid-sm text-ink-500" role="alert">
            {t("AcceptInvite.blockedBody", { email: invite?.email })}
          </p>
        )}

        {phase === "done" && (
          <p className="mt-2 text-fluid-sm text-ink-500">
            {t("AcceptInvite.doneBody")}
          </p>
        )}
      </div>

      {phase === "form" && (
        <AcceptForm token={token} onDone={() => setPhase("done")} />
      )}

      {phase === "confirm" && (
        <ConfirmLink
          token={token}
          invite={invite}
          onDone={() => setPhase("done")}
        />
      )}

      {phase === "done" && (
        <Link to="/login" className={`${primaryButtonClass} mt-7`}>
          {t("AcceptInvite.continueToLogin")}
        </Link>
      )}

      {(phase === "invalid" || phase === "blocked") && (
        <Link to="/login" className={`${primaryButtonClass} mt-7`}>
          {t("AcceptInvite.backToLogin")}
        </Link>
      )}

      {isActing && (
        <span className="mt-6 block text-center text-fluid-sm text-ink-500">
          {t("AcceptInvite.backToLoginPrefix")}{" "}
          <Link to="/login" className={textLinkClass}>
            {t("AcceptInvite.backToLoginLink")}
          </Link>
        </span>
      )}
    </AuthShell>
  );
};

/** Brand-new account: collect a name and password, then accept. */
const AcceptForm = ({ token, onDone }) => {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validationError, setValidationError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (password.length < MIN_PASSWORD_LENGTH) {
      setValidationError(
        t("AcceptInvite.form.passwordTooShort", { count: MIN_PASSWORD_LENGTH }),
      );
      return;
    }

    if (password !== confirmPassword) {
      setValidationError(t("AcceptInvite.form.passwordMismatch"));
      return;
    }

    setValidationError("");
    setSubmitting(true);

    try {
      await AcceptResourceInviteAction({ token, firstName, lastName, password });
      onDone();
    } catch (error) {
      setValidationError(
        error.response?.data?.message || t("AcceptInvite.genericError"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-8">
      <div className="form-row grid gap-4 sm:grid-cols-2">
        <div className="form-item">
          <label htmlFor="accept-invite-first-name" className={labelClass}>
            {t("AcceptInvite.form.firstName")}
          </label>
          <input
            type="text"
            id="accept-invite-first-name"
            name="firstName"
            className={fieldClass}
            placeholder={t("AcceptInvite.form.firstNamePlaceholder")}
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
            autoComplete="given-name"
            required
          />
        </div>
        <div className="form-item">
          <label htmlFor="accept-invite-last-name" className={labelClass}>
            {t("AcceptInvite.form.lastName")}
          </label>
          <input
            type="text"
            id="accept-invite-last-name"
            name="lastName"
            className={fieldClass}
            placeholder={t("AcceptInvite.form.lastNamePlaceholder")}
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
            autoComplete="family-name"
            required
          />
        </div>
      </div>

      <div className="form-item mt-4">
        <label htmlFor="accept-invite-password" className={labelClass}>
          {t("AcceptInvite.form.password")}
        </label>
        <input
          type="password"
          id="accept-invite-password"
          name="password"
          className={fieldClass}
          placeholder={t("AcceptInvite.form.password")}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
          minLength={MIN_PASSWORD_LENGTH}
          required
        />
        <p className="mt-1.5 text-xs text-ink-400">
          {t("AcceptInvite.form.passwordHint", { count: MIN_PASSWORD_LENGTH })}
        </p>
      </div>

      <div className="form-item mt-4">
        <label htmlFor="accept-invite-confirm-password" className={labelClass}>
          {t("AcceptInvite.form.confirmPassword")}
        </label>
        <input
          type="password"
          id="accept-invite-confirm-password"
          name="confirmPassword"
          className={validationError ? fieldErrorClass : fieldClass}
          placeholder={t("AcceptInvite.form.confirmPassword")}
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          autoComplete="new-password"
          aria-invalid={validationError ? "true" : undefined}
          aria-describedby={
            validationError ? "accept-invite-password-error" : undefined
          }
          required
        />
        {validationError && (
          <div
            id="accept-invite-password-error"
            role="alert"
            className="error-message mt-1.5 flex items-center gap-1.5 text-xs font-medium text-red-600"
          >
            <i className="bi bi-exclamation-circle-fill" aria-hidden="true" />
            {validationError}
          </div>
        )}
      </div>

      <div className="form-item mt-7">
        <button type="submit" className={primaryButtonClass} disabled={submitting}>
          {submitting ? t("AcceptInvite.form.submitting") : t("AcceptInvite.form.submit")}
        </button>
      </div>
    </form>
  );
};

/** Existing same-organization account: no fields, just confirm the link. */
const ConfirmLink = ({ token, invite, onDone }) => {
  const { t } = useTranslation();
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleClick = async () => {
    setError("");
    setSubmitting(true);
    try {
      await AcceptResourceInviteAction({ token });
      onDone();
    } catch (err) {
      setError(err.response?.data?.message || t("AcceptInvite.genericError"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-7">
      {invite?.email && (
        <p className="rounded-lg bg-ink-50 px-4 py-3 text-center text-fluid-sm text-ink-700">
          {t("AcceptInvite.confirm.body", {
            email: invite.email,
            organizationName: invite.organizationName,
          })}
        </p>
      )}

      {error && (
        <div
          role="alert"
          className="error-message mt-3 flex items-center gap-1.5 text-xs font-medium text-red-600"
        >
          <i className="bi bi-exclamation-circle-fill" aria-hidden="true" />
          {error}
        </div>
      )}

      <button
        type="button"
        className={`${primaryButtonClass} mt-4`}
        onClick={handleClick}
        disabled={submitting}
      >
        {submitting ? t("AcceptInvite.confirm.submitting") : t("AcceptInvite.confirm.submit")}
      </button>
    </div>
  );
};

export default AcceptInvite;
